import PropTypes from 'prop-types'
import { compose, setDisplayName, withHandlers, withState, pure } from 'recompose'
import { get, find, isArray } from 'lodash'

import { PLACEMENT_POSITIONS } from 'utils/constants/components/popover'
import { LIST_SIZES, LIST_TYPES } from 'utils/constants/components/dropdown'
import { DropdownInfiniteListWithSearchView } from 'components/dropdown/infiniteListWithSearch/DropdownInfiniteListWithSearchView'

const MIN_SEARCH_LENGTH = 3
const SEARCH_DEBOUNCE_TIMEOUT = 600

/*
 * search would apply if no previous value
 * or if previous value differ from current value
 * set `highlightString` if search applied
 */
const handleSearch =
  ({ setHighlightString, searchString, listMeta, onSearch }) =>
  () => {
    const prevSearchString = get(listMeta, 'filters.q', '')
    const prevSearchStringTrimmed = prevSearchString.trim()
    const currentSearchStringTrimmed = searchString.trim()

    if (!prevSearchString || prevSearchStringTrimmed !== currentSearchStringTrimmed) {
      setHighlightString(searchString)
      onSearch(currentSearchStringTrimmed)
    }
  }

/*
 * pass input value to container above
 * loading new list once if `value` length less than the limit
 * or if value was erase all at once
 * reset `highlightString` if search applied
 */
const handleInputChange =
  ({ searchLimit, onSearch, searchString, setHighlightString, setSearchString }) =>
  value => {
    setSearchString(value)

    const currentValueLength = value.length
    const prevValueLength = searchString.length

    if (
      (prevValueLength > currentValueLength && searchLimit - currentValueLength === 1) ||
      prevValueLength - currentValueLength > 1
    ) {
      setHighlightString('')
      onSearch('')
    }
  }

const handleClear =
  ({ inputRef, onClear }) =>
  () => {
    inputRef && inputRef.focus()
    onClear && onClear()
  }

const handleIsRowSelected =
  ({ value, isRowSelectedFn }) =>
  ({ item }) => {
    if (!isRowSelectedFn) {
      return isArray(value) ? !!find(value, { id: get(item, 'id') }) : get(value, 'id') === get(item, 'id')
    }

    return isRowSelectedFn({ item })
  }

export const DropdownInfiniteListWithSearch = compose(
  setDisplayName('DropdownInfiniteListWithSearch'),
  pure,
  withState('searchString', 'setSearchString', ''),
  withState('highlightString', 'setHighlightString', ''),
  withHandlers({
    handleSearch,
    handleInputChange,
    handleClear,
    handleIsRowSelected,
  }),
)(DropdownInfiniteListWithSearchView)

DropdownInfiniteListWithSearch.propTypes = {
  // common
  isLoading: PropTypes.bool,
  size: PropTypes.oneOf(LIST_SIZES),
  titleKey: PropTypes.string,
  type: PropTypes.oneOf(LIST_TYPES),

  // dropdown
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  onClose: PropTypes.func,
  placement: PropTypes.oneOf(PLACEMENT_POSITIONS),
  triggerClassName: PropTypes.string,
  triggerElementRenderer: PropTypes.func,
  width: PropTypes.number,

  // trigger
  isDisabled: PropTypes.bool,
  isValid: PropTypes.bool,
  multiSelectTitle: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),

  // list
  closeOnSelect: PropTypes.bool,
  emptyMessage: PropTypes.string,
  isRowSelectedFn: PropTypes.func,
  height: PropTypes.number,
  isEmptyStateIconShown: PropTypes.bool,
  isMultiSelect: PropTypes.bool,
  listItems: PropTypes.array,
  listMaxHeight: PropTypes.number,
  listMeta: PropTypes.object,
  noRowGutter: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onRowClick: PropTypes.func,
  rowHeight: PropTypes.number,
  rowRenderer: PropTypes.func,
  totalCount: PropTypes.number,

  // search
  debounceTimeout: PropTypes.number,
  isAutoFocus: PropTypes.bool,
  onClear: PropTypes.func,
  onSearch: PropTypes.func,
  searchLimit: PropTypes.number,
  searchString: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  headerRenderer: PropTypes.func,

  // footer
  error: PropTypes.string,
  errorClassName: PropTypes.string,
  footerRenderer: PropTypes.func,
  showErrorText: PropTypes.bool,

  // reposition
  keysToPositionUpdate: PropTypes.array,
}

DropdownInfiniteListWithSearch.defaultProps = {
  debounceTimeout: SEARCH_DEBOUNCE_TIMEOUT,
  searchLimit: MIN_SEARCH_LENGTH,
}
